import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  CircularProgress, 
  IconButton, 
  Card, 
  CardContent,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button
} from "@mui/material";
import { 
  ChevronLeft, 
  BarChart3, 
  ClipboardList, 
  Calendar, 
  Clock, 
  ChevronDown,
  Activity,
  UserCheck
} from "lucide-react";
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip as RechartsTooltip, 
  Legend 
} from "recharts";
import { kioskModel } from "../Axios/AxiosConfig";

const COLORS = {
  Vata: "#75623F",
  Pitta: "#FF5C00",
  Kapha: "#6CBA6C",
  Ama: "#DA6E0B"
};

const CATEGORY_ICONS = {
  Vata: <Activity size={20} />,
  Pitta: <Activity size={20} />,
  Kapha: <Activity size={20} />,
  Ama: <Activity size={20} />
};

const MaharishiQuizResult = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState([]);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(0);

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        const res = await kioskModel.get(`api/v1/getMaharishiQuizByUID/${uid}`);
        if (res.status === 200) {
          const data = Array.isArray(res.data) ? res.data : [res.data];
          setQuizData(data.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)));
        }
      } catch (err) {
        console.error("Error fetching quiz data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [uid]);

  const currentSession = quizData[selectedSessionIndex];

  // Group answers by category
  const groupedAnswers = useMemo(() => {
    if (!currentSession?.answers) return {};
    return currentSession.answers.reduce((acc, curr) => {
      const cat = curr.categoryName || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(curr);
      return acc;
    }, {});
  }, [currentSession]);

  const chartData = useMemo(() => {
    if (!currentSession?.results) return [];
    return Object.entries(currentSession.results)
      .map(([name, value]) => ({ name, value: Number(value) }))
      .filter(d => d.value > 0);
  }, [currentSession]);

  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen bg-[#FDFDFD]">
        <CircularProgress sx={{ color: "#DA6E0B" }} />
        <Typography className="mt-4 text-[#918165] font-medium animate-pulse">Analyzing Quiz Records...</Typography>
      </Box>
    );
  }

  if (!quizData || quizData.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen p-8 text-center bg-[#FDFDFD]">
        <img src="/icons/NoAppointment.png" alt="No data" className="w-48 mb-6 opacity-50" />
        <Typography variant="h5" className="text-[#3B362A] font-bold mb-2">No Quiz Records Found</Typography>
        <Typography className="text-[#918165] mb-6">This patient hasn't completed the Maharishi Quiz yet.</Typography>
        <Button 
          variant="outlined" 
          startIcon={<ChevronLeft />} 
          onClick={() => navigate(-1)}
          sx={{ borderColor: "#DA6E0B", color: "#DA6E0B", borderRadius: "10px", textTransform: "none" }}
        >
          Back to Profile
        </Button>
      </Box>
    );
  }

  return (
    <Box className="bg-[#F8F8F4] min-h-screen" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Premium Sticky Header */}
      <Box className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-bottom border-[#E7E7DB] px-8 py-4 flex items-center justify-between shadow-sm">
        <Box className="flex items-center gap-4">
          <IconButton 
            onClick={() => navigate(-1)}
            className="hover:bg-[#FBF1E7] transition-colors"
          >
            <ChevronLeft color="#DA6E0B" />
          </IconButton>
          <Box>
            <Typography variant="h5" className="font-bold text-[#3B362A]">
              Maharishi Quiz Analysis
            </Typography>
            <Box className="flex items-center gap-2">
              <UserCheck size={14} className="text-[#DA6E0B]" />
              <Typography variant="caption" className="text-[#918165] font-medium">
                UID: {uid} • Latest Submission: {new Date(quizData[0].date || quizData[0].createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Chip 
          label={`${quizData.length} Total Quizzes`} 
          sx={{ bgcolor: "#FBF1E7", color: "#DA6E0B", fontWeight: 700, borderRadius: "8px" }} 
        />
      </Box>

      <Grid container sx={{ p: 4 }} spacing={4}>
        {/* Sidebar - Timeline History */}
        <Grid item xs={12} md={3}>
          <Typography variant="overline" className="text-[#918165] font-bold tracking-widest mb-4 block px-2">
            QUIZ HISTORY TIMELINE
          </Typography>
          <Box className="relative pl-4 space-y-6 before:absolute before:left-[21px] before:top-0 before:bottom-0 before:w-[2px] before:bg-[#E7E7DB]">
            {quizData.map((session, index) => (
              <Box 
                key={session.id || index}
                onClick={() => setSelectedSessionIndex(index)}
                className={`relative pl-8 cursor-pointer group transition-all`}
              >
                {/* Dot */}
                <Box className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 z-10 transition-all ${
                  selectedSessionIndex === index 
                  ? "bg-[#DA6E0B] border-[#DA6E0B] scale-125" 
                  : "bg-white border-[#C6B897] group-hover:border-[#DA6E0B]"
                }`} />
                
                <Paper className={`p-4 rounded-xl transition-all border ${
                  selectedSessionIndex === index 
                  ? "bg-white border-[#DA6E0B] shadow-lg translate-x-2" 
                  : "bg-transparent border-transparent hover:bg-white hover:border-[#E7E7DB]"
                }`}>
                  <Typography className={`text-sm font-bold ${selectedSessionIndex === index ? "text-[#DA6E0B]" : "text-[#3B362A]"}`}>
                    {new Date(session.date || session.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                  </Typography>
                  <Box className="flex items-center gap-2 mt-1">
                    <Clock size={12} className="text-[#918165]" />
                    <Typography variant="caption" className="text-[#918165]">
                      {new Date(session.date || session.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Main Workspace */}
        <Grid item xs={12} md={9}>
          {/* Summary Dashboard */}
          <Grid container spacing={4} className="mb-8">
            <Grid item xs={12} lg={6}>
              <Card className="rounded-3xl border-none shadow-xl bg-white h-full overflow-hidden">
                <Box className="bg-[#3B362A] p-4 text-white flex items-center justify-between">
                  <Typography variant="subtitle1" className="font-bold flex items-center gap-2">
                    <BarChart3 size={20} /> Dosha Distribution
                  </Typography>
                  <Chip label="Percentage Based" size="small" sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)", border: "1px solid" }} />
                </Box>
                <CardContent className="p-8">
                  <Box style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={100}
                          paddingAngle={8}
                          dataKey="value"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[entry.name] || "#C6B897"} stroke="none" />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Box className="grid grid-cols-2 gap-4 h-full">
                {Object.entries(currentSession.results || {}).map(([key, value]) => (
                  <Card key={key} className="rounded-3xl border-none shadow-md hover:shadow-lg transition-shadow bg-white flex items-center">
                    <CardContent className="w-full p-6 text-center">
                      <Box 
                        className="mx-auto w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                        style={{ backgroundColor: `${COLORS[key]}15`, color: COLORS[key] }}
                      >
                        {CATEGORY_ICONS[key]}
                      </Box>
                      <Typography variant="overline" className="text-[#918165] font-bold">{key}</Typography>
                      <Typography variant="h4" className="font-black" style={{ color: COLORS[key] }}>
                        {value}<span className="text-sm font-normal">%</span>
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Categorized Detailed Responses */}
          <Typography variant="h6" className="text-[#3B362A] font-bold mb-4 px-2 flex items-center gap-2">
            <ClipboardList size={22} className="text-[#DA6E0B]" />
            Detailed Question Analysis
          </Typography>

          <Box className="space-y-4">
            {Object.entries(groupedAnswers).map(([category, answers]) => (
              <Accordion 
                key={category} 
                className="rounded-2xl border-none shadow-sm before:hidden overflow-hidden"
                defaultExpanded={category === "Vata"}
              >
                <AccordionSummary 
                  expandIcon={<ChevronDown color="#DA6E0B" />}
                  className="bg-white border-b border-[#F8F8F4]"
                >
                  <Box className="flex items-center gap-3">
                    <Box 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${COLORS[category]}15`, color: COLORS[category] }}
                    >
                      {CATEGORY_ICONS[category]}
                    </Box>
                    <Typography className="font-bold text-[#3B362A]">{category} Section</Typography>
                    <Chip 
                      label={`${answers.length} Questions`} 
                      size="small" 
                      className="ml-2"
                      sx={{ fontSize: "10px", fontWeight: 700 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails className="p-0 bg-white">
                  <Box className="divide-y divide-[#F8F8F4]">
                    {answers.map((ans, idx) => (
                      <Box key={ans.questionId} className="p-6 hover:bg-[#FDFDFD] transition-colors">
                        <Box className="flex justify-between items-start gap-4 mb-4">
                          <Typography className="text-[#3B362A] font-medium leading-relaxed flex-1">
                            {idx + 1}. {ans.question}
                          </Typography>
                          <Typography variant="h5" className="font-black text-[#DA6E0B] leading-none">
                            {ans.answer}
                            <span className="text-xs text-[#918165] font-medium ml-1">/ 6</span>
                          </Typography>
                        </Box>
                        
                        {/* Score Visualization */}
                        <Box className="flex items-center gap-4">
                          <Box className="flex-1 h-3 bg-[#F1EFEC] rounded-full overflow-hidden flex gap-[2px]">
                            {[...Array(6)].map((_, i) => (
                              <Box 
                                key={i}
                                className="flex-1 transition-all duration-500"
                                style={{ 
                                  backgroundColor: i < ans.answer ? COLORS[category] : "transparent",
                                  opacity: i < ans.answer ? (1 - (i * 0.1)) : 1
                                }}
                              />
                            ))}
                          </Box>
                          <Typography variant="caption" className="font-bold text-[#918165] min-w-[100px] text-right uppercase">
                            {ans.answer === 0 ? "Not at all" : ans.answer === 6 ? "Strongly" : "Moderate"}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MaharishiQuizResult;
